import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {parseAccountFilter} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {
  buildContactDisplayHandle,
  buildContactDisplayName,
} from '../../../../../helpers/models/contactHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {MessageModel} from '../../../../../models/api/messageModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {ContactCardLocationsEnum} from '../../../../../store/panels/contacts/contactCard/contactCardPanelState';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../store/panels/panelStateBase';
import {ContactCardWrapperContainer} from '../../../../contactCard/contactCardWrapperContainer';
import {handleRenderers} from '../../conversation/viewerConstants';

/*
 * Props.
 */

export interface MessageViewerRecipientProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | ContactDetailsPanelId | AccountDetailsPanelId;
  searchQuery?: string;
  recipient: VisualRecipientModel;
  isUnverified?: boolean;
  message?: MessageModel;
  hasMoreRecipients?: boolean;
  isBold?: boolean;
  needsParentHover?: boolean;
  searchableAttributes: Record<string, boolean>;
  account?: AccountModel;
  shouldHideClickableUntilHover?: boolean;
}

/*
 * Style.
 */

interface ButtonStyleProps {
  $needsParentHover: boolean;
  $shouldHideClickableUntilHover?: boolean;
}
const StyledButtonDiv = styled.div<ButtonStyleProps>`
  display: inline-block;
  ${(p) => maybeAddParentLogicStyles(p.$needsParentHover, p.$shouldHideClickableUntilHover)};
`;

function maybeAddParentLogicStyles(needsParentHover: boolean, shouldHideClickableUntilHover?: boolean) {
  // Basic behavior, we're always blue.
  if (!needsParentHover && !shouldHideClickableUntilHover) {
    return css`
      color: ${(p) => p.theme.interactiveText};
    `;
  }
  // If we are hiding clickable styles, we should only show them when hovering.
  if (!needsParentHover && shouldHideClickableUntilHover) {
    return css`
      &:hover {
        color: ${(p) => p.theme.interactiveText};
      }
    `;
  }

  // Advanced behavior, we're blue on parent hover.
  return css`
    color: ${(p) => p.theme.greys.shade80};

    ${hoverSelector} {
      color: ${(p) => p.theme.interactiveText};
    }
  `;
}

interface NameStyleProps {
  $hasHover?: boolean;
  $isBold?: boolean;
  $shouldHideClickableUntilHover?: boolean;
}

const StyledDetailsWrapperDiv = styled.div<NameStyleProps>`
  color: ${(p) => p.theme.message.header};
  display: inline-flex;
  flex-flow: row;
  font-weight: ${(p) => (p.$isBold ? fontWeights.semibold : fontWeights.normal)};

  ${(p) => maybeAddHoverStyles(p)};
`;

const StyledAccountDiv = styled.div`
  margin-left: 4px;
`;

interface StyledAccountNameDivProps {
  $hasHighlight: boolean;
}

const StyledAccountNameDiv = styled.div<StyledAccountNameDivProps>`
  display: inline-block;

  &::after {
    content: attr(data-account-name);
  }

  background-color: ${(p) => (p.$hasHighlight ? '#fff627' : 'transparent')};
`;

function maybeAddHoverStyles(props: NameStyleProps) {
  if (props.$hasHover) {
    return css`
      color: ${(p) => p.theme.interactiveTextHover};
    `;
  }

  if (props.$shouldHideClickableUntilHover) {
    return '';
  }

  return css`
    &:hover {
      color: ${(p) => p.theme.interactiveTextHover};
    }
  `;
}

const StyledHandleSpan = styled.span`
  color: ${(p) => p.theme.greys.shade80};
`;

/*
 * Component.
 */

export const MessageViewerRecipient: FC<MessageViewerRecipientProps> = (props) => {
  const {recipient, hasMoreRecipients, needsParentHover, shouldHideClickableUntilHover} = props;

  return (
    <>
      <StyledButtonDiv
        $needsParentHover={Boolean(needsParentHover)}
        $shouldHideClickableUntilHover={shouldHideClickableUntilHover}
      >
        <PopoverCoordinator
          renderButton={(isOpen) => renderName(isOpen, props)}
          renderModal={(modalProps) => renderRecipientDropdown(modalProps, props)}
        />
      </StyledButtonDiv>
      {maybeRenderHandle(recipient, props)}
      {hasMoreRecipients && <>,&nbsp;</>}
    </>
  );
};

/*
 * Button.
 */

function renderName(isOpen: boolean, props: MessageViewerRecipientProps) {
  const {recipient, searchableAttributes, shouldHideClickableUntilHover} = props;
  const name = buildContactDisplayName(recipient);

  return (
    <StyledDetailsWrapperDiv
      $hasHover={isOpen}
      $isBold={props.isBold}
      $shouldHideClickableUntilHover={shouldHideClickableUntilHover}
      {...searchableAttributes}
    >
      <div>{name}</div>
      {maybeRenderAccount(props)}
    </StyledDetailsWrapperDiv>
  );
}

function maybeRenderAccount(props: MessageViewerRecipientProps) {
  const {account, searchQuery} = props;

  if (!account) {
    return null;
  }

  const hasHighlight = computeAccountHasHighlight(account, searchQuery);

  return (
    <StyledAccountDiv>
      (<StyledAccountNameDiv data-account-name={account.name} $hasHighlight={hasHighlight} />)
    </StyledAccountDiv>
  );
}

function maybeRenderHandle(recipient: VisualRecipientModel, props: MessageViewerRecipientProps) {
  // If the name and handle are the same, nothing to do.
  const handle = buildContactDisplayHandle(recipient);
  const name = buildContactDisplayName(recipient);
  if (handle === name) {
    return null;
  }

  // The contact source changes the way we display the handle.
  const handleRenderer = handleRenderers[recipient.contact.source];
  if (!handleRenderer) {
    return null;
  }

  const {searchableAttributes} = props;

  // Otherwise, render the handle.
  return (
    <StyledHandleSpan {...searchableAttributes}>
      &nbsp;
      {handleRenderer(handle)}
    </StyledHandleSpan>
  );
}

/*
 * Dropdown.
 */

function renderRecipientDropdown(modalProps: PopoverTransferProps, props: MessageViewerRecipientProps) {
  return (
    <ContactCardWrapperContainer
      popoverProps={modalProps}
      visualContact={props.recipient}
      isUnverified={props.isUnverified}
      panelId={props.panelId}
      location={ContactCardLocationsEnum.MESSAGE_VIEWER}
    />
  );
}

/*
 * Helpers.
 */

/* Determine if this account name matches the current search term, and if so highlight it. */
function computeAccountHasHighlight(account: AccountModel, searchQuery?: string) {
  if (!searchQuery) {
    return false;
  }

  const accountSearchTerm = parseAccountFilter(searchQuery);
  return account.alias === accountSearchTerm;
}
